import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useRealtimeWorkflows } from '@/hooks/useRealtimeWorkflows';
import { useRoles } from '@/hooks/useRoles';
import { useLabOS } from '@/lib/labos';
import WorkflowBrowser from '@/components/WorkflowBrowser';
import HScrollRow from '@/components/HScrollRow';
import { Loader2, Workflow, Clock, X, RefreshCw, EyeOff, ChevronDown } from 'lucide-react';

export default function GalleryPage() {
  const navigate = useNavigate();
  const {
    workflows,
    executions,
    loading,
    error,
    setStatusFilter,
    setAccessOnly,
    cancelExecution,
    refreshWorkflows,
    refreshExecutionOutputs,
    toggleHideOutput,
  } = useRealtimeWorkflows();
  // Track executions already asked to re-sign, to avoid repeat requests.
  const refreshedOutputsRef = useRef<Set<string>>(new Set());
  const { roles } = useRoles();
  const { user } = useLabOS();
  const [cancelingIds, setCancelingIds] = useState<Set<string>>(new Set());
  const [refreshing, setRefreshing] = useState(false);
  const [hidingOutputs, setHidingOutputs] = useState<Set<string>>(new Set());
  const [latestOutputsExpanded, setLatestOutputsExpanded] = useState(true);

  useEffect(() => {
    // Home shows only published workflows the user actually has access to —
    // even admins/managers, so it stays an end-user surface, not a management view.
    setStatusFilter('published');
    setAccessOnly(true);
  }, [setStatusFilter, setAccessOnly]);

  const handleCancelExecution = async (promptId: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (window.confirm('Cancel this execution?')) {
      setCancelingIds((prev) => new Set(prev).add(promptId));
      try {
        await cancelExecution(promptId);
      } catch {
        alert('Failed to cancel execution');
      } finally {
        setCancelingIds((prev) => {
          const newSet = new Set(prev);
          newSet.delete(promptId);
          return newSet;
        });
      }
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await refreshWorkflows();
    setRefreshing(false);
  };

  const handleToggleHideOutput = async (executionId: string, filename: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const outputKey = `${executionId}-${filename}`;
    setHidingOutputs((prev) => new Set(prev).add(outputKey));
    try {
      await toggleHideOutput(executionId, filename, true);
    } catch (err) {
      console.error('Failed to hide output:', err);
    } finally {
      setHidingOutputs((prev) => {
        const next = new Set(prev);
        next.delete(outputKey);
        return next;
      });
    }
  };

  const allPendingExecutions = executions.filter(
    (e) => e.status === 'queued' || e.status === 'running' || e.status === 'localizing',
  );
  const completedOutputs = executions.filter((e) => e.status === 'completed' && e.outputs.some((o) => !o.hidden)).slice(0, 5);
  const hasHiddenOutputs = executions.some((e) => e.status === 'completed' && e.outputs.some((o) => o.hidden));

  // Re-sign output URLs for the completed executions previewed here; the fresh
  // URLs persist to Firestore and return via the realtime listener.
  useEffect(() => {
    for (const exec of completedOutputs) {
      if (refreshedOutputsRef.current.has(exec.id)) continue;
      refreshedOutputsRef.current.add(exec.id);
      refreshExecutionOutputs(exec.id);
    }
  }, [completedOutputs, refreshExecutionOutputs]);

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-gradient-to-br from-background via-background to-accent/30 dark:to-accent/10">
      <div className="flex-1 overflow-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
          <header className="flex items-center justify-between gap-3 mb-6">
            <h1 className="text-xl font-bold tracking-tight text-foreground truncate">
              AI Creative Studio
              <span className="text-muted-foreground font-light"> · Workflow Runner</span>
            </h1>
            {roles.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5 shrink-0">
                {roles.map((role) => (
                  <span
                    key={role}
                    className="text-[11px] px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300"
                  >
                    {role}
                  </span>
                ))}
              </div>
            )}
          </header>

          {error && (
            <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg mb-8">
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          {workflows.length === 0 ? (
            <div className="text-center py-24">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-accent mb-6">
                <Workflow className="h-10 w-10 text-muted-foreground/40" />
              </div>
              <h3 className="text-lg font-medium text-muted-foreground">No workflows available yet</h3>
              <p className="text-sm text-muted-foreground/60 mt-1">Ask your admin to publish some workflows</p>
            </div>
          ) : (
            <div className="space-y-8">
              {/* In-progress executions — horizontal scroll row */}
              {allPendingExecutions.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Loader2 className="h-4 w-4 animate-spin text-violet-500" />
                    <h2 className="text-sm font-semibold text-foreground">In Progress ({allPendingExecutions.length})</h2>
                  </div>
                  <HScrollRow>
                    {allPendingExecutions.slice(0, 12).map((exec) => (
                      <div key={exec.id} className="relative group shrink-0 w-56">
                        <Link
                          to={`/history/${exec.id}`}
                          className={
                            'flex items-center gap-2 px-3 py-2 rounded-xl border border-violet-300/40 ' +
                            'dark:border-violet-500/30 bg-violet-50/50 dark:bg-violet-500/10 ' +
                            'hover:bg-violet-100/50 dark:hover:bg-violet-500/20 transition-colors'
                          }
                        >
                          <div className="w-1.5 h-1.5 rounded-full bg-violet-500 animate-pulse shrink-0" />
                          <span className="text-xs font-medium text-foreground truncate flex-1">{exec.workflowName}</span>
                          <Badge variant="secondary" className="text-[9px] px-1.5 py-0 shrink-0">
                            {exec.status === 'queued' ? 'queued' : exec.status === 'localizing' ? 'receiving' : 'running'}
                          </Badge>
                        </Link>
                        <button
                          onClick={(e) => handleCancelExecution(exec.id, e)}
                          disabled={cancelingIds.has(exec.id)}
                          className={
                            'absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-destructive hover:bg-destructive/90 ' +
                            'flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50'
                          }
                          title="Cancel execution"
                        >
                          {cancelingIds.has(exec.id) ? (
                            <Loader2 className="h-3 w-3 text-destructive-foreground animate-spin" />
                          ) : (
                            <X className="h-3 w-3 text-destructive-foreground" />
                          )}
                        </button>
                      </div>
                    ))}
                  </HScrollRow>
                </div>
              )}

              {/* Latest outputs — collapsible */}
              {(completedOutputs.length > 0 || hasHiddenOutputs) && (
                <div>
                  <button
                    type="button"
                    onClick={() => setLatestOutputsExpanded(!latestOutputsExpanded)}
                    className="flex items-center justify-between w-full pb-3 group"
                  >
                    <h2 className="text-sm font-semibold text-foreground">Latest Outputs ({completedOutputs.length})</h2>
                    <div className="flex items-center gap-2">
                      <Link
                        to="/history"
                        className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                        onClick={(e) => e.stopPropagation()}
                      >
                        View all
                      </Link>
                      <ChevronDown
                        className={`h-4 w-4 transition-transform duration-200 ${latestOutputsExpanded ? 'rotate-0' : '-rotate-90'}`}
                      />
                    </div>
                  </button>
                  {latestOutputsExpanded &&
                    (completedOutputs.length > 0 ? (
                      <HScrollRow>
                        {completedOutputs.map((exec) => {
                          const visibleOutputs = exec.outputs.filter((o) => !o.hidden);
                          return (
                            <div
                              key={exec.id}
                              className={
                                'group relative shrink-0 w-64 rounded-xl border border-border/40 overflow-hidden bg-background cursor-pointer ' +
                                'hover:border-violet-400/40 transition-colors flex flex-col'
                              }
                              onClick={() => navigate(`/history/${exec.id}`)}
                            >
                              <div className="aspect-video overflow-hidden flex gap-0.5 bg-black/5 dark:bg-white/5">
                                {visibleOutputs.slice(0, 3).map((output, oi) => {
                                  const isVideo = output.filename.endsWith('.mp4') || output.filename.endsWith('.webm');
                                  const isImage =
                                    output.filename.endsWith('.png') ||
                                    output.filename.endsWith('.jpg') ||
                                    output.filename.endsWith('.jpeg') ||
                                    output.filename.endsWith('.webp');
                                  return (
                                    <div key={oi} className="flex-1 overflow-hidden">
                                      {isImage && (
                                        <img src={output.url} alt={output.filename} className="w-full h-full object-cover" />
                                      )}
                                      {isVideo && (
                                        <video
                                          src={output.url}
                                          className="w-full h-full object-cover"
                                          muted
                                          onMouseEnter={(e) => (e.target as HTMLVideoElement).play()}
                                          onMouseLeave={(e) => {
                                            const v = e.target as HTMLVideoElement;
                                            v.pause();
                                            v.currentTime = 0;
                                          }}
                                        />
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                              <div className="px-2.5 py-2 space-y-0.5">
                                <p className="text-[11px] font-medium text-foreground truncate">{exec.workflowName}</p>
                                <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                                  <span>
                                    {visibleOutputs.length} {visibleOutputs.length === 1 ? 'file' : 'files'}
                                  </span>
                                  {exec.completedAt && (
                                    <span className="flex items-center gap-0.5">
                                      <Clock className="h-2.5 w-2.5" />
                                      {Math.round(
                                        (new Date(exec.completedAt).getTime() - new Date(exec.createdAt).getTime()) / 1000,
                                      )}
                                      s
                                    </span>
                                  )}
                                </div>
                              </div>
                              {/* Hide button for first visible output */}
                              {visibleOutputs.length > 0 && (
                                <button
                                  type="button"
                                  title="Hide from My Outputs"
                                  onClick={(e) => handleToggleHideOutput(exec.id, visibleOutputs[0].filename, e)}
                                  disabled={hidingOutputs.has(`${exec.id}-${visibleOutputs[0].filename}`)}
                                  className={
                                    'absolute top-2 right-2 z-10 p-1.5 rounded-lg ' +
                                    'bg-black/50 text-white hover:bg-black/70 backdrop-blur-sm transition-all ' +
                                    'opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto ' +
                                    'disabled:opacity-50'
                                  }
                                >
                                  {hidingOutputs.has(`${exec.id}-${visibleOutputs[0].filename}`) ? (
                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                  ) : (
                                    <EyeOff className="h-3.5 w-3.5" />
                                  )}
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </HScrollRow>
                    ) : (
                      <div className="text-center py-12 px-4">
                        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-accent mb-4">
                          <EyeOff className="h-8 w-8 text-muted-foreground/40" />
                        </div>
                        <h3 className="text-sm font-medium text-muted-foreground">All outputs are hidden</h3>
                        <p className="text-xs text-muted-foreground/60 mt-1">
                          Complete a workflow to see outputs here, or view{' '}
                          <Link to="/history" className="text-violet-500 hover:underline">
                            hidden outputs in History
                          </Link>
                        </p>
                      </div>
                    ))}
                </div>
              )}

              {/* All workflows — full-width browser (favorites live in the browser's sidebar filter) */}
              <div className="relative">
                <div className="flex items-center justify-end mb-4">
                  <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing || loading}>
                    <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                </div>
                {refreshing && (
                  <div className="absolute inset-0 z-10 flex items-start justify-center pt-16 bg-background/50 backdrop-blur-[1px]">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                )}
                <WorkflowBrowser
                  workflows={workflows}
                  currentUserId={user?.uid}
                  showScope={false}
                  singleCategory
                  linkTo={(wf) => `/run/${wf.id}`}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
